import React from 'react';
import Link from 'next/link';
import { Star, Store, Truck, TrendingDown, ShieldCheck } from 'lucide-react';
import {
  PRICE_LISTINGS,
  RETAILERS,
  KIBBLE_TYPE_LABELS,
  AGE_RANGE_LABELS,
  type Product,
  type DogSize,
} from '@/lib/mockData';

const SIZE_BADGE: Record<DogSize, string> = {
  XS: 'bg-pink-100 text-pink-700',
  S: 'bg-yellow-100 text-yellow-700',
  M: 'bg-orange-100 text-orange-700',
  L: 'bg-teal-100 text-teal-700',
  XL: 'bg-blue-100 text-blue-700',
};

export default function ProductCard({ product }: { product: Product }) {
  const listings = PRICE_LISTINGS.filter(
    (l) => l.productId === product.id && l.stockStatus !== 'out-of-stock'
  ).sort((a, b) => a.price - b.price);

  const best = listings[0];
  const second = listings[1];
  const retailerCount = new Set(listings.map((l) => l.retailerId)).size;
  const bestRetailer = RETAILERS.find((r) => r.id === best?.retailerId);
  const hasFreeShipping = listings.some((l) => l.shippingCost === null);

  const priceDiff = best && second ? (second.price - best.price).toFixed(2) : null;

  return (
    <Link
      href={`/product-detail-price-comparison?id=${product.id}`}
      className="card flex flex-col group hover:border-orange-200 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
    >
      {/* Image */}
      <div className="relative h-40 bg-gradient-to-br from-orange-50 to-amber-50 overflow-hidden">
        <img
          src={product.imageUrl}
          alt={product.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {hasFreeShipping && (
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-green-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
            <Truck size={10} />
            Livraison offerte
          </div>
        )}
        {priceDiff && parseFloat(priceDiff) > 3 && (
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-red-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
            <TrendingDown size={10} />
            -{priceDiff} € vs le + cher
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <p className="text-xs font-bold text-orange-500 uppercase tracking-wide mb-0.5">{product.brand}</p>
        <h3 className="font-display font-bold text-gray-900 text-sm leading-tight line-clamp-2 group-hover:text-orange-600 transition-colors mb-2">
          {product.name}
        </h3>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {product.dogSizes.map((size) => (
            <span key={`card-${product.id}-size-${size}`} className={`badge-size text-xs ${SIZE_BADGE[size]}`}>
              {size}
            </span>
          ))}
          <span className="badge-size text-xs bg-gray-100 text-gray-600">
            {KIBBLE_TYPE_LABELS[product.kibbleType]}
          </span>
          <span className="badge-size text-xs bg-gray-100 text-gray-600">
            {AGE_RANGE_LABELS[product.ageRange]}
          </span>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 mb-3">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star
                key={`card-${product.id}-star-${s}`}
                size={11}
                className={s <= Math.round(product.ratings) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}
              />
            ))}
          </div>
          <span className="text-xs text-gray-500">{product.ratings} · {product.reviewCount.toLocaleString('fr-FR')} avis</span>
        </div>

        {/* Price section */}
        <div className="mt-auto border-t border-orange-50 pt-3">
          <div className="flex items-end justify-between gap-2">
            <div>
              <p className="text-xs text-gray-400">Meilleur prix</p>
              <div className="flex items-baseline gap-1.5">
                <span className="font-display text-xl font-extrabold text-orange-600 tabular-nums">
                  {best ? `${best.price.toFixed(2)} €` : '—'}
                </span>
              </div>
              {best && (
                <p className="text-xs text-gray-500 tabular-nums">
                  {best.pricePerKg.toFixed(2)} €/kg · {best.weightKg} kg
                </p>
              )}
            </div>
            <div className="text-right shrink-0">
              {bestRetailer && (
                <div className="flex items-center gap-1 justify-end">
                  {bestRetailer.trustBadge && <ShieldCheck size={12} className="text-teal-500" />}
                  <span className="text-xs font-bold" style={{ color: bestRetailer.logoColor }}>
                    {bestRetailer.name}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1 justify-end mt-0.5">
                <Store size={11} className="text-gray-400" />
                <span className="text-xs text-gray-400">{retailerCount} marchand{retailerCount > 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>

          <div className="mt-3 w-full bg-orange-500 group-hover:bg-orange-600 text-white text-sm font-semibold py-2 rounded-xl text-center transition-colors duration-150">
            Voir les {retailerCount} prix →
          </div>
        </div>
      </div>
    </Link>
  );
}